import { computed, Injectable, signal } from '@angular/core';

export enum CreationSourceEnum {
    MAP = 'map',
    TABULAR = 'tabular',
}

export type CreationContextParams = {
    depositIds: string[];
    entityId: string;
    layerIds: string[];
};

export type CreationContext = {
    depositIds: string[];
    entityId: string | null;
    layerIds: string[];
    source: CreationSourceEnum | null;
};

@Injectable({
    providedIn: 'root',
})
export class EntityDetailCreationContextService {
    public readonly depositIds = computed(() => this._depositIds());
    public readonly entityId = computed(() => this._entityId());
    public readonly isEditingExistingCreatedEntity = signal(false);
    public readonly isLayerEditable = computed(() => this._isLayerEditable());
    public readonly isMapCreation = computed(() => this._source() === CreationSourceEnum.MAP);
    public readonly isTabularCreation = computed(() => this._source() === CreationSourceEnum.TABULAR);
    public readonly layerIds = computed(() => this._layerIds());
    public readonly shouldAutoSave = computed(() => this._shouldAutoSave());
    public readonly source = computed(() => this._source());

    private readonly _depositIds = signal<string[]>([]);
    private readonly _entityId = signal<string | null>(null);
    private readonly _isLayerEditable = signal<boolean>(true);
    private readonly _layerIds = signal<string[]>([]);
    private readonly _shouldAutoSave = signal(true);
    private readonly _source = signal<CreationSourceEnum | null>(null);
    private currentCreationFinalizer: (() => void) | null = null;

    public clearCurrentCreationFinalizer(finalizer?: () => void): void {
        if (!this.currentCreationFinalizer || (finalizer && this.currentCreationFinalizer !== finalizer)) {
            return;
        }

        this.currentCreationFinalizer = null;
    }

    public disableAutoSave(): void {
        this._shouldAutoSave.set(false);
    }

    public enableAutoSave(): void {
        this._shouldAutoSave.set(true);
    }

    public finalizeCurrentCreationNow(): void {
        if (!this.currentCreationFinalizer) {
            return;
        }

        this.currentCreationFinalizer();
    }

    public initEditingExistingCreatedEntity(params: CreationContextParams, isLayerEditable: boolean): void {
        this._entityId.set(params.entityId);
        this._layerIds.set(params.layerIds);
        this._depositIds.set(params.depositIds);
        this._source.set(null);
        this._isLayerEditable.set(isLayerEditable);
        this.isEditingExistingCreatedEntity.set(true);
        this.disableAutoSave();
    }

    public initFromMap(params: CreationContextParams): void {
        this._entityId.set(params.entityId);
        this._layerIds.set(params.layerIds);
        this._depositIds.set(params.depositIds);
        this._source.set(CreationSourceEnum.MAP);

        this._isLayerEditable.set(true);
        this.isEditingExistingCreatedEntity.set(false);

        this.enableAutoSave();
    }

    public initFromTabular(params: CreationContextParams): void {
        this._entityId.set(params.entityId);
        this._layerIds.set(params.layerIds);
        this._depositIds.set(params.depositIds);
        this._source.set(CreationSourceEnum.TABULAR);

        this._isLayerEditable.set(true);
        this.isEditingExistingCreatedEntity.set(false);

        this.enableAutoSave();
    }

    public registerCurrentCreationFinalizer(finalizer: () => void): void {
        this.currentCreationFinalizer = finalizer;
    }

    public reset(): void {
        this.clearContext();
        this.enableAutoSave();
    }

    public resetPreservingAutoSaveState(): void {
        this.clearContext();
    }

    private clearContext(): void {
        this._entityId.set(null);
        this._layerIds.set([]);
        this._depositIds.set([]);
        this._source.set(null);
        this._isLayerEditable.set(true);

        this.isEditingExistingCreatedEntity.set(false);
    }
}
